import type { ActiveInspection } from '../models/activeInspection'
import type { TranslateFn } from './activeInspectionCells'
import { formatDay, formatServices, formatStateBadgeLabel } from './activeInspectionCells'

/** The sort the table is under: which column, which way. Mirrors React Admin's `SortPayload`. */
export interface ActiveInspectionSort {
  field: string
  order: 'ASC' | 'DESC'
}

/** The columns of the exported file, in the order they are written. */
export const ACTIVE_INSPECTION_EXPORT_COLUMNS = [
  'day',
  'container',
  'manual',
  'sostrat',
  'location',
  'inspectionLevel',
  'services',
  'state',
  'statusId',
  'photos',
  'seals',
] as const

export type ActiveInspectionExportColumn = (typeof ACTIVE_INSPECTION_EXPORT_COLUMNS)[number]

export interface ActiveInspectionExport {
  /** One translated header per column, in column order. */
  headers: string[]
  /** One row per inspection, in the order given, each cell in column order. */
  rows: string[][]
}

/**
 * Translation key of each column's header. The columns the table also shows
 * reuse the table's own header, so the file and the screen name a column the
 * same way; the ones the table has no header for (the origin and the SOSTRAT
 * sighting sit inside the Contenedor cell as the marker and its colour, the raw
 * status on a tooltip, the counts nowhere) get a header of their own under
 * `export`.
 */
const HEADER_KEYS: Record<ActiveInspectionExportColumn, string> = {
  day: 'resources.inspections.fields.day',
  container: 'resources.inspections.fields.container',
  manual: 'resources.inspections.export.fields.manual',
  sostrat: 'resources.inspections.export.fields.sostrat',
  location: 'resources.inspections.fields.location',
  inspectionLevel: 'resources.inspections.fields.inspection_level',
  services: 'resources.inspections.fields.services',
  state: 'resources.inspections.fields.state',
  statusId: 'resources.inspections.export.fields.status',
  photos: 'resources.inspections.export.fields.photos',
  seals: 'resources.inspections.export.fields.seals',
}

/**
 * A column's value, as text. Text columns carry what the table displays for the
 * cell; the origin and the SOSTRAT sighting, which the table shows as one glyph
 * and its colour, each become the yes/no word of the active language; the counts are the evidence counts of the delete
 * confirmation, which count every photo and every seal, unclassified seals
 * included — not the per-step counts of the strip, which drop unclassified
 * seals and are emptied on a cancelled inspection.
 */
const cellOf = (translate: TranslateFn, inspection: ActiveInspection, column: ActiveInspectionExportColumn): string => {
  switch (column) {
    case 'day':
      return formatDay(inspection.day)
    case 'container':
      return inspection.container
    case 'manual':
      return translate(inspection.isLocal ? 'resources.inspections.export.yes' : 'resources.inspections.export.no')
    case 'sostrat':
      return translate(inspection.seenInSostrat ? 'resources.inspections.export.yes' : 'resources.inspections.export.no')
    case 'location':
      return translate(`resources.historical_inspections.enums.location.${inspection.location}`)
    case 'inspectionLevel':
      // The full name, which the table keeps on the chip's tooltip: a file has
      // no hover, and the short code says nothing about a transvase level.
      return translate(`resources.historical_inspections.enums.inspection_level.${inspection.inspectionLevel}`)
    case 'services':
      return formatServices(inspection.services)
    case 'state':
      return formatStateBadgeLabel(translate, inspection)
    case 'statusId':
      return inspection.statusId
    case 'photos':
      return String(inspection.evidenceCounts.photos)
    case 'seals':
      return String(inspection.evidenceCounts.seals)
  }
}

/**
 * Order records the way React Admin's `useList` orders them under a sort.
 *
 * `useList` sorts its data with a plain `>` / `<` comparison of the sorted
 * field and hands back only the current page, so the whole set in the table's
 * order has to be rebuilt with the same comparison. `Array.prototype.sort` is
 * stable, so records that tie keep the order they arrived in.
 */
export const sortRecords = <T extends object>(records: T[], sort: ActiveInspectionSort): T[] => {
  // A field that is not on the record sorts as empty, which is as far as
  // useList's `get` would take it too.
  const valueOf = (record: T): string | number | boolean =>
    (record as unknown as Record<string, string | number | boolean | undefined>)[sort.field] ?? ''

  return [...records].sort((a, b) => {
    if (valueOf(a) > valueOf(b)) {
      return sort.order === 'ASC' ? 1 : -1
    }
    if (valueOf(a) < valueOf(b)) {
      return sort.order === 'ASC' ? -1 : 1
    }
    return 0
  })
}

/**
 * Order the inspections the way the table orders them under the same sort,
 * from the same array the table was given.
 */
export const sortActiveInspections = (inspections: ActiveInspection[], sort: ActiveInspectionSort): ActiveInspection[] =>
  sortRecords(inspections, sort)

/**
 * The file's content for a set of inspections: the translated headers and one
 * row per inspection, in the order given. The caller hands in the filtered,
 * sorted array the table renders, so the file is that table and nothing else.
 */
export const buildActiveInspectionExport = (inspections: ActiveInspection[], translate: TranslateFn): ActiveInspectionExport => ({
  headers: ACTIVE_INSPECTION_EXPORT_COLUMNS.map(column => translate(HEADER_KEYS[column])),
  rows: inspections.map(inspection => ACTIVE_INSPECTION_EXPORT_COLUMNS.map(column => cellOf(translate, inspection, column))),
})

/** A field is quoted only when it has to be: a comma, a quote or a line break inside it. */
const escapeCsvField = (value: string): string => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value)

/** Serialise headers and rows as RFC 4180 CSV: comma-separated, one line per row. */
export const toCsv = ({ headers, rows }: ActiveInspectionExport): string =>
  [headers, ...rows].map(row => row.map(escapeCsvField).join(',')).join('\n')
